import { Injectable, inject } from '@angular/core';
import { environment } from '../../../environments/environment';
import { Observable, forkJoin } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseRequester } from './common/base-requester';
import moment, { Moment } from 'moment';
import { UnifiedQueryV2Service } from './unified-query-v2.service';
import {
    get as _get,
    set as _set,
    find as _find,
    each as _each,
    isNil as _isNil,
    isNil,
} from 'lodash';

@Injectable({
    providedIn: 'root',
})
export class SaleAnalyticService extends BaseRequester {
    private unifiedQueryV2Service = inject(UnifiedQueryV2Service);

    get(platforms: AnalyticPlatform[]): Observable<any> {
        const inventoryDataObservable = this.unifiedQueryV2Service.query({
            db: 'lnw_crawler',
            col: 'inventory',
            query: {
                is_show: true,
                stock_status: { $ne: 'discontinued' },
            },
            options: {
                projection: {
                    sku: 1,
                    title: 1,
                    price: 1,
                    remain: 1,
                },
            },
        });

        // Each window needs its own moment instance: subtract()/startOf()
        // mutate in place, so sharing one would compound the offsets.
        const saleSinceObservables = {
            last1MDate: this.getSaleSince(
                moment().subtract(1, 'month'),
                platforms,
            ),
            last3MDate: this.getSaleSince(
                moment().subtract(3, 'months'),
                platforms,
            ),
            last6MDate: this.getSaleSince(
                moment().subtract(6, 'months'),
                platforms,
            ),
            lastYtdDate: this.getSaleSince(moment().startOf('year'), platforms),
            last1YDate: this.getSaleSince(
                moment().subtract(1, 'year'),
                platforms,
            ),
            last2YDate: this.getSaleSince(
                moment().subtract(2, 'years'),
                platforms,
            ),
            last3YDate: this.getSaleSince(
                moment().subtract(3, 'years'),
                platforms,
            ),
            last10YDate: this.getSaleSince(
                moment().subtract(10, 'years'),
                platforms,
            ),
        };
        const saleSinceData = forkJoin(saleSinceObservables);
        const lastSaleDateData = this.getAllProductLastSaleDate(platforms);
        return forkJoin({
            inventoryData: inventoryDataObservable,
            saleSinceData: saleSinceData,
            lastSaleDateData: lastSaleDateData,
        }).pipe(
            map((result: AnalyticCombine) => {
                const data: any[] = [];
                _each(result.inventoryData, (inventoryRecord) => {
                    const saleSince = {};
                    _each(saleSinceObservables, (_, key) => {
                        const saleNumber: number = this.getSale(
                            result.saleSinceData[key],
                            inventoryRecord['_id'],
                        );
                        _set(saleSince, key, saleNumber);
                    });
                    inventoryRecord['price'] = parseFloat(
                        _get(inventoryRecord, 'price.$numberDecimal'),
                    );
                    inventoryRecord['saleSince'] = saleSince;
                    inventoryRecord['lastSaleDate'] = this.getLastSaleDate(
                        result.lastSaleDateData,
                        inventoryRecord['_id'],
                    );
                    data.push(inventoryRecord);
                });
                return data;
            }),
        );
    }

    private getSale(saleSinceData: SaleSinceRecord[], pid: number): number {
        const saleRecord: SaleSinceRecord | undefined = _find(saleSinceData, {
            _id: pid,
        });
        const totalSale = _get(saleRecord, 'total_sale');
        if (_isNil(totalSale)) {
            return 0;
        }
        // The sale-since lambda now returns total_sale as a plain number
        // (see temp/sale-since-README.md); tolerate the legacy Decimal128
        // ($numberDecimal) wire format as a fallback.
        const value =
            typeof totalSale === 'object'
                ? _get(totalSale, '$numberDecimal')
                : totalSale;
        const parsed = typeof value === 'string' ? parseFloat(value) : value;
        return _isNil(parsed) || Number.isNaN(parsed) ? 0 : parsed;
    }

    private getLastSaleDate(
        lastSaleDateData: LastSaleRecord[],
        pid: number,
    ): Moment | null {
        const saleDateObj: LastSaleRecord | undefined = _find(
            lastSaleDateData,
            {
                _id: pid,
            },
        );
        const saleDateIso: string | undefined = _get(saleDateObj, 'last_sale');
        if (!isNil(saleDateIso)) {
            const saleDate: Moment = moment(saleDateIso);
            if (saleDate.isValid()) {
                return saleDate;
            }
        }
        return null;
    }

    private getSaleSince(
        date: Moment,
        platforms: AnalyticPlatform[],
    ): Observable<any> {
        return this.internalPostRequest<SaleSinceRequest>(
            environment.saleSinceEndpoint,
            {
                after_iso_date: date.toISOString(),
                platforms: platforms,
            },
        ).pipe(map((response) => this.serviceHandler<any>(response)));
    }

    private getAllProductLastSaleDate(
        platforms: AnalyticPlatform[],
    ): Observable<any> {
        return this.internalPostRequest<LastSaleRequest>(
            environment.lastSaleEndpoint,
            { platforms },
        ).pipe(map((response) => this.serviceHandler<any>(response)));
    }
}

// Values must match the platform keys the sale-since / last-sale lambdas
// accept (SUPPORTED_PLATFORMS in their app.js; unknown names fail
// validation). The lambda key is 'TikTokShop' — the lambda itself maps it
// to the raw 'TikTok Shop' order.salesChannel string — see
// src/app/pages/reordering/DOWNSTREAM-NOTES.md §7: splitting it out also
// narrows what 'LnwShop' (the catch-all channel) returns.
export enum AnalyticPlatform {
    LnwShop = 'LnwShop',
    Lazada = 'Lazada',
    Shopee = 'Shopee',
    TikTokShop = 'TikTokShop',
}

interface SaleSinceRequest {
    after_iso_date: string;
    query?: object;
    platforms: AnalyticPlatform[];
}

interface LastSaleRequest {
    query?: object;
    platforms: AnalyticPlatform[];
}

interface AnalyticCombine {
    inventoryData: any;
    saleSinceData: any;
    lastSaleDateData: LastSaleRecord[];
}

interface SaleSinceRecord {
    _id: number;
    // Plain number under the current lambda contract; the object form is the
    // legacy Decimal128 shape, still accepted defensively by getSale().
    total_sale: number | { $numberDecimal: string };
}

interface LastSaleRecord {
    _id: number;
    last_sale: string;
}
